import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Divider, 
  FormControl, 
  Select, 
  MenuItem, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  getCodingFlowDashboard, 
  getCodingFlowVisitDetailsMulti
} from '../../api/codingFlowApis';
import PurpleVisitDetails from './PurpleVisitDetails';

const PurpleCDDC = ({ patientId, username, accessRole }) => {
  const [dashboardData, setDashboardData] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [visitDetails, setVisitDetails] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [days, setDays] = useState(30);
  const [allVisitDetails, setAllVisitDetails] = useState([]); // Store all visit details

  // For admins, show all data. For regular users, filter by username
  const displayData = accessRole === 'admin' ? dashboardData : 
    dashboardData.filter(item => item.provider_username === username);

  // Single useEffect that fetches dashboard data only
  useEffect(() => {
    if (username && accessRole) {
      fetchDashboardData();
    }
  }, [days, username, accessRole]);

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);
      const token = localStorage.getItem('token') || 
                   document.cookie.split('; ').find(row => row.startsWith('authToken='))?.split('=')[1];
      
      // Single API call to get dashboard data with corrected counts
      const dashboardResult = await getCodingFlowDashboard({
        days: days,
        username: username,  
        token: token,
        includeDetails: true // Add this flag if the backend supports it
      });
      
      setDashboardData(dashboardResult);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData([]);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Helper function to get metric types for aggregated metrics
  const getMetricTypes = (metric) => {
    const metricMapping = {
      'accepted_codes': ['accepted_codes', 'accepted_recapture', 'accepted_allymar'],
      'dismissed_codes': ['dismissed_codes', 'dismissed_recapture', 'dismissed_allymar'],
      'unattended_codes': ['unattended_codes', 'unattended_recapture', 'unattended_allymar'],
      'accepted_codes_pending': ['accepted_codes_pending', 'accepted_recapture_pending', 'accepted_allymar_pending'],
      'dismissed_codes_pending': ['dismissed_codes_pending', 'dismissed_recapture_pending', 'dismissed_allymar_pending'],
      'unattended_codes_pending': ['unattended_codes_pending', 'unattended_recapture_pending', 'unattended_allymar_pending'],
      'existing_unrefreshed_codes': ['existing_unrefreshed_codes'], // Add this new metric
      // Single metrics remain as arrays with one item
      'patients_visits': ['patients_visits'],
      'visits_with_new_codes': ['visits_with_new_codes'],
      'visits_without_new_codes': ['visits_without_new_codes'],
      'new_codes': ['new_codes'],
      'doctor_codes': ['doctor_codes'],
      'coder_codes': ['coder_codes']
    };
    
    return metricMapping[metric] || [metric];
  };

  const handleMetricClick = async (department, provider, metric, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    
    const buttonKey = `${department}-${provider}-${metric}`;
    
    try {
      setLoadingButton(buttonKey);
      const token = localStorage.getItem('token') || 
                   document.cookie.split('; ').find(row => row.startsWith('authToken='))?.split('=')[1];
      
      // Get the metric types for this metric
      const metricTypes = getMetricTypes(metric);
      
      const data = await getCodingFlowVisitDetailsMulti({
        department: department,
        provider: provider,
        metric: metricTypes, // Pass array instead of single metric
        days: days,
        username: username,  
        token: token
      });
      
      setVisitDetails(data);
      setSelectedTask({ department, provider, metric });
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setLoadingButton(null);
    }
  };

  // Helper function to check if a specific button is loading
  const isButtonLoading = (department, provider, metric) => {
    return loadingButton === `${department}-${provider}-${metric}`;
  };

  const handleRefreshVisitDetails = async () => {
    if (selectedTask) {
      try {
        setLoadingButton(`${selectedTask.department}-${selectedTask.provider}-${selectedTask.metric}`);
        const token = localStorage.getItem('token') || 
                     document.cookie.split('; ').find(row => row.startsWith('authToken='))?.split('=')[1];
        
        // Get the metric types for the selected task
        const metricTypes = getMetricTypes(selectedTask.metric);
        
        const data = await getCodingFlowVisitDetailsMulti({
          department: selectedTask.department,
          provider: selectedTask.provider,
          metric: metricTypes, // Pass array instead of single metric
          days: days,
          username: username,
          token: token
        });
        
        setVisitDetails(data);
      } catch (error) {
        console.error('Error refreshing visit details:', error);
      } finally {
        setLoadingButton(null);
      }
    }
  };

  // Remove the recalculation logic since counts should come correctly from backend
  const getDisplayMetrics = (providerData) => {
    return providerData; // Use the data as-is from the backend
  };

  if (dashboardLoading) return <Typography>Loading...</Typography>;
  if (!username || !accessRole) return <Typography>Missing authentication details</Typography>;

  if (displayData.length === 0) {
    return (
      <div>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: '#d5dbdb', margin: '0 !important', minHeight: 'unset !important', '& .MuiAccordionSummary-content': { margin: '5px 0 !important' } }}
          >
            <Typography variant="h6">CDDC Summary</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: '#eaeded', padding: '12px' }}>
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                No coding data available for the selected time period
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Divider sx={{ height: 15 }} />
      </div>
    );
  }

  return (
    <div>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ backgroundColor: '#d5dbdb', margin: '0 !important', minHeight: 'unset !important', '& .MuiAccordionSummary-content': { margin: '5px 0 !important' } }}
        >
          <Typography variant="h6">CDDC Summary</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: '#eaeded', padding: '12px' }}>
          {/* Time Period Selector */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="body2" sx={{ mr: 2, fontWeight: 'bold' }}>
                Time Period:
              </Typography>
              <FormControl variant="standard" sx={{ minWidth: 120 }}>
                <Select
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value))}
                  sx={{ fontSize: '14px' }}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={14}>Last 14 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={60}>Last 60 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {accessRole === 'admin' 
                ? `Showing data for all providers (${displayData.length} providers)`
                : `Showing data for provider: ${username}`
              }
            </Typography>
          </Box>

          {displayData.map((providerData, index) => {
            const displayMetrics = getDisplayMetrics(providerData);
            
            return (
              <div key={index}>
                {/* Provider Header */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bgcolor="purple"
                  color="white"
                  p={1}
                  borderRadius="4px 4px 0 0"
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {providerData.department} - {providerData.provider}
                    {(accessRole === 'admin' || accessRole === 'pcp') && (
                      <Typography component="span" sx={{ fontSize: '0.8em', ml: 1 }}>
                        ({providerData.provider_username})
                      </Typography>
                    )}
                  </Typography>
                </Box>

                {/* Metrics Table */}
                <TableContainer component={Paper}>
                  <Table aria-label="coding metrics table">
                    <TableBody>
                      {/* Patient Visits */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Patient Visits
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{providerData.patients_visits || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'patients_visits')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'patients_visits', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'patients_visits') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Spacer */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                      </TableRow>

                      {/* Visits with New Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Visits with New Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{providerData.visits_with_new_codes || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'visits_with_new_codes')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'visits_with_new_codes', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'visits_with_new_codes') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Visits without New Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Visits without New Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{providerData.visits_without_new_codes || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'visits_without_new_codes')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'visits_without_new_codes', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'visits_without_new_codes') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Spacer */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                      </TableRow>

                      {/* New Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Total New Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{providerData.new_codes || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'new_codes')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'new_codes', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'new_codes') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Doctor Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Doctor Added Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{providerData.doctor_codes || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'doctor_codes')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'doctor_codes', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'doctor_codes') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Coder Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Coder Added Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{providerData.coder_codes || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'coder_codes')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'coder_codes', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'coder_codes') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Resolved Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Resolved
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f9f9f9' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{displayMetrics.resolved || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'resolved')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'resolved', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'resolved') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Spacer */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                      </TableRow>

                      {/* Accepted Codes */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Accepted but not Assessed Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#fef9e7' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{displayMetrics.accepted_codes_pending + displayMetrics.accepted_recapture_pending + displayMetrics.accepted_allymar_pending|| 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'accepted_codes_pending')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'accepted_codes_pending', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'accepted_codes_pending') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Existing Unrefreshed Codes - NEW ROW */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Unrefreshed Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f8c5adff' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{displayMetrics.existing_unrefreshed_codes_pending || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'existing_unrefreshed_codes_pending')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'existing_unrefreshed_codes_pending', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'existing_unrefreshed_codes_pending') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Dismissed Codes - Updated to use recalculated metrics */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Dismissed Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#f8c5adff' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{displayMetrics.dismissed_codes_pending + displayMetrics.dismissed_recapture_pending + displayMetrics.dismissed_allymar_pending || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'dismissed_codes_pending')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'dismissed_codes_pending', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'dismissed_codes_pending') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Unattended Codes - Updated to use recalculated metrics */}
                      <TableRow>
                        <TableCell sx={{ padding: '8px', bgcolor: 'lightgray', fontWeight: 'bold' }}>
                          Unattended Codes
                        </TableCell>
                        <TableCell sx={{ padding: '8px', bgcolor: '#fef9e7' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color="primary">{displayMetrics.unattended_codes_pending + displayMetrics.unattended_recapture_pending + displayMetrics.unattended_allymar_pending || 0}</Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={isButtonLoading(providerData.department, providerData.provider, 'unattended_codes_pending')}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMetricClick(providerData.department, providerData.provider, 'unattended_codes_pending', event);
                              }}
                            >
                              {isButtonLoading(providerData.department, providerData.provider, 'unattended_codes_pending') ? 'Loading...' : 'View Details'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {index < displayData.length - 1 && <Divider sx={{ height: 15 }} />}
              </div>
            );
          })}
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ height: 15 }} />

      {/* Visit Details Modal */}
      <PurpleVisitDetails
        open={showDetails}
        onClose={() => setShowDetails(false)}
        visitDetails={visitDetails}
        selectedTask={selectedTask}
        username={username}
        token={localStorage.getItem('token') || 
              document.cookie.split('; ').find(row => row.startsWith('authToken='))?.split('=')[1]}
        onRefresh={handleRefreshVisitDetails}
      />
    </div>
  );
};

export default PurpleCDDC;